import { advance, dayOf, hourOf, progress, type LifeEvent, type LifeState } from './life.ts';
import { Rng } from './rng.ts';
import { homeCountry, type Country } from './travel.ts';
import { hereCountry } from './character.ts';
import { voiceOf } from './voice.ts';

/**
 * The social account on the phone. Photos are real captures of the world (taken by the game);
 * the simulation decides who sees them and how they react, trickling likes, comments and
 * followers in over the following hours. Posting well feeds the social need and the social skill,
 * and a growing following is a resource the rest of the life can use.
 */

/**
 * A song you can put on a post (and the car radio plays). CC0 tracks from OpenGameArt, credited by their real
 * title and creator (public/CREDITS.md in the game). `id` is the file (audio/music/<id>.mp3); `clip` where the
 * post's few seconds start. Old saves' posts carry made-up songs with no id: shown, never played.
 */
export interface Song { id?: string; title: string; artist: string; genre?: 'afro' | 'lofi' | 'funk' | 'beats' | 'reggae' | 'café' | 'pop' | 'jazz'; clip?: number; /** the tempo, and when the first beat falls (s): the club dances to it */ bpm?: number; beat?: number }
export const SONGS: Song[] = [
  { id: 'afro-028', title: '028', artist: 'some-weirdo', genre: 'afro', clip: 14, bpm: 105.0, beat: 0.534 },
  { id: 'chill-lofi', title: 'Chill Lofi', artist: 'omfgdude', genre: 'lofi', clip: 8 },
  { id: 'lofi-hip-hop', title: 'Lofi Hip Hop', artist: 'omfgdude', genre: 'lofi', clip: 20 },
  { id: 'lofi-loop', title: 'Lofi Hip Hop Loop', artist: 'omfgdude', genre: 'lofi', clip: 4 },
  { id: 'raspberry-jam', title: 'Raspberry Jam', artist: 'congusbongus', genre: 'funk', clip: 22, bpm: 112.3, beat: 0.531 },
  { id: 'rhythm-garden', title: 'Rhythm Garden', artist: 'congusbongus', genre: 'funk', clip: 30, bpm: 116.0, beat: 0.206 },
  { id: 'beats-2', title: 'Looping Beats II', artist: 'bretbernhoft', genre: 'beats', clip: 10, bpm: 103.5, beat: 0.197 },
  { id: 'reggae', title: 'Reggae', artist: 'pro-sensory', genre: 'reggae', clip: 25 },
  { id: 'tango', title: 'Tango', artist: 'umplix', genre: 'café', clip: 6 },
  { id: 'accordion', title: 'The Accordion Sample', artist: 'spring-spring', genre: 'café', clip: 12 },
  // full length (CC0, OpenGameArt): the radio's Hits and Jazz & Soul stations, more for the others
  { id: 'shortcuts', title: 'Shortcuts', artist: 'Zane Little Music', genre: 'pop', clip: 40 },
  { id: 'the-way-it-is', title: 'The Way It Is', artist: 'Zane Little Music', genre: 'pop', clip: 30 },
  { id: 'rhythm-factory', title: 'Rhythm Factory', artist: 'Zane Little Music', genre: 'funk', clip: 30 },
  { id: 'midnight-cruiser', title: 'Midnight Cruiser', artist: 'Zane Little Music', genre: 'jazz', clip: 20 },
  { id: 'apple-cider', title: 'Apple Cider', artist: 'Zane Little Music', genre: 'lofi', clip: 25 },
  { id: 'funky-lofi-jam', title: 'Funky Hip Hop Lofi Jam', artist: 'omfgdude', genre: 'beats', clip: 20 },
  { id: 'soul-release', title: 'Soul Release', artist: 'Ruskerdax', genre: 'jazz', clip: 35 },
  { id: 'november-snow', title: 'November Snow', artist: 'cynicmusic', genre: 'lofi', clip: 45 },
  { id: 'napping-cloud', title: 'Napping on a Cloud', artist: 'congusbongus', genre: 'lofi', clip: 30 },
  { id: 'two-left-socks', title: 'Two Left Socks', artist: 'congusbongus', genre: 'café', clip: 20 },
];

/**
 * The game's people reacting on Social (likes and comments on your posts from your contacts and strangers, the made-up
 * counts on the city's pages, their answers to your comments): archived, 2026-10-29. Likes and comments are real
 * players' only. Off, nothing new is made up, and what was is kept on each post (npcLikes, npcComments) but not shown
 * (archiveNpcReactions, once). Followers still come.
 */
export const NPC_REACTIONS = false;

export interface Comment { name: string; text: string; t: number; /** you wrote it */ mine?: boolean }
export interface Post {
  id: string;
  t: number;
  caption: string;
  song: Song | null;
  place: string;
  /** who held the camera: you, or the stranger you asked */
  takenBy: string;
  /** 0..1 from the game: framing, light, the person actually in the photo */
  quality: number;
  /** a reel: a few seconds of video (kept on the device by the same id), not a still */
  reel?: boolean;
  likes: number;
  comments: Comment[];
  newFollowers: number;
  reach: number;
  /** you reshared someone's post of you (an agency's shoot) */
  repostOf?: { author: string; handle: string };
  /** a carousel: more pictures after the first (photo ids), swiped through */
  more?: string[];
  /** archived: the game's people's likes and comments (NPC_REACTIONS) */
  npcLikes?: number;
  npcComments?: Comment[];
}

export interface SocialState {
  handle: string;
  followers: number;
  posts: Post[];
  /** reactions still arriving */
  queue: { at: number; postId: string; likes: number; follows: number; comment?: Comment }[];
  placesPosted: string[];
  photosAsked: number;
  /** the game's people's reactions put away (archiveNpcReactions) */
  npcArchived?: boolean;
}

/** a post's comments kept (the newest), and strangers who comment on one post at most */
export const POST_COMMENTS = 60, STRANGER_COMMENTS = 30;
export function socialOf(s: LifeState): SocialState {
  if (s.social) trimSocial(s.social);
  if (!s.social) {
    const r = new Rng(s.seed ^ 0x51ed);
    const first = s.card.name.split(' ')[0].toLowerCase();
    const bg = { struggling: 0, working: 40, comfortable: 140, wealthy: 600 }[s.card.background.id] ?? 0;
    s.social = {
      handle: `${first}${r.pick(['_', '.', ''])}${r.pick(['official', 'x', String(r.int(1, 99)), 'lagos', 'thegreat', ''])}`,
      followers: Math.round(30 + s.skills.social * 3 + bg + r.int(0, 60)),
      posts: [], queue: [], placesPosted: [], photosAsked: 0,
    };
  }
  return s.social;
}

/**
 * Ask someone on the street to take your picture. Most people in Lagos will, if you ask nicely
 * and you don't look like trouble; some won't.
 */
export function askForPhoto(s: LifeState, _name: string): { yes: boolean; line: string; events: LifeEvent[] } {
  const soc = socialOf(s);
  soc.photosAsked++;
  const r = new Rng((s.seed ^ Math.imul(s.minutes + soc.photosAsked, 2654435761)) >>> 0);
  const h = hourOf(s.minutes);
  let chance = 0.55 + s.skills.social / 250 + (s.dressedDay === dayOf(s.minutes) ? 0.08 : 0);
  if (s.needs.hygiene < 25) chance -= 0.25;
  if (h >= 21 || h < 6) chance -= 0.2; // late at night people are wary
  const yes = r.chance(Math.max(0.1, Math.min(0.92, chance)));
  const line = yes
    ? r.pick(voiceOf(hereCountry(s)).photoYes) // (a stranger here talks like here)
    : r.pick(voiceOf(hereCountry(s)).photoNo);
  s.needs.social = Math.min(100, s.needs.social + (yes ? 5 : 1));
  const events = advance(s, 2);
  if (yes) events.push(...progress(s, 'photo_asked'));
  return { yes, line, events };
}

/** the handles of strangers who find your posts (where you live) */
const HANDLES: Record<Country, string[]> = {
  nigeria: ['tobi', 'ada', 'kels', 'mide', 'zainab', 'uche', 'bolu', 'sharon', 'chi', 'femi'],
  france: ['lea', 'hugo', 'manon', 'theo', 'ines', 'lucas', 'chloe', 'yanis', 'camille', 'jules'],
  uae: ['sara', 'omar', 'layla', 'khalid', 'noor', 'zayed', 'maryam', 'ahmed', 'dana', 'rashid'],
  italy: ['giulia', 'lorenzo', 'sofia', 'matteo', 'chiara', 'leo', 'bea', 'tommi', 'vale', 'fra'],
  usa: ['jess', 'mike', 'tay', 'jordan', 'ashley', 'tyler', 'mads', 'chris', 'kayla', 'devon'],
  china: ['xiaoyu', 'lili', 'haoran', 'mengmeng', 'jiahui', 'zihan', 'weiwei', 'tiantian', 'yuchen', 'kiki'],
  japan: ['yuki', 'haruto', 'sakura', 'ren', 'aoi', 'sota', 'mio', 'kaito', 'hina', 'riku'],
};

/**
 * Older lives with a big following queued a comment for every forty likes: thousands, megabytes (too big to save to an
 * account). Down to what's ever shown: the newest POST_COMMENTS on a post, STRANGER_COMMENTS waiting per post.
 */
function trimSocial(soc: SocialState) {
  if (soc.queue.length > 400) {
    const per = new Map<string, number>();
    soc.queue = soc.queue.filter((q) => { if (!q.comment) return true; const n = (per.get(q.postId) ?? 0) + 1; per.set(q.postId, n); return n <= STRANGER_COMMENTS + 6; });
  }
  for (const p of soc.posts) if (p.comments.length > POST_COMMENTS) p.comments.splice(0, p.comments.length - POST_COMMENTS);
}

/** Post a photo. The game passes how good the photo actually is; the audience does the rest. */
export function postPhoto(s: LifeState, p: { id: string; caption: string; song: Song | null; place: string; takenBy: string; quality: number; more?: string[]; reel?: boolean }): LifeEvent[] {
  const soc = socialOf(s);
  const r = new Rng((s.seed ^ Math.imul(s.minutes + soc.posts.length * 7919, 2246822519)) >>> 0);
  const h = hourOf(s.minutes);
  // what makes a post travel: the photo itself, a new place, posting at a good hour, a song, a caption
  const novelty = soc.placesPosted.includes(p.place) ? 0.75 : 1.2;
  const prime = (h >= 18 && h <= 23) || (h >= 12 && h <= 14) ? 1.25 : h < 6 ? 0.6 : 1;
  const extras = (p.song ? 1.1 : 1) * (p.caption.trim().length > 3 ? 1.08 : 0.95) * (p.more?.length ? 1 + Math.min(4, p.more.length) * 0.04 : 1) * (p.reel ? 1.45 : 1); // (a carousel keeps people on it a little longer; a reel travels furthest)
  const fatigue = soc.posts.filter((x) => s.minutes - x.t < 1440).length * 0.15; // posting five times a day annoys people
  const q = 0.35 + p.quality * 1.3;
  const reach = Math.round(soc.followers * r.range(0.18, 0.32) * q * novelty * prime * extras * Math.max(0.4, 1 - fatigue) + r.int(0, 6));
  const likes = NPC_REACTIONS ? Math.round(reach * r.range(0.35, 0.6) * (0.6 + p.quality * 0.6)) : 0; // (off: likes are players')
  const follows = Math.round(reach * 0.035 * q * novelty + (p.quality > 0.75 && r.chance(0.08) ? r.int(20, 120) : 0));
  const post: Post = { ...p, t: s.minutes, likes: 0, comments: [], newFollowers: 0, reach };
  soc.posts.unshift(post);
  if (!soc.placesPosted.includes(p.place)) soc.placesPosted.push(p.place);

  // reactions arrive over the next few hours: a burst first, then a tail
  const comments: Comment[] = [];
  if (NPC_REACTIONS) {
  const family = s.contacts.filter((c) => ['Mother', 'Father', 'Brother', 'Sister'].includes(c.relation));
  const friends = s.contacts.filter((c) => !['Mother', 'Father', 'Brother', 'Sister', 'Son', 'Daughter'].includes(c.relation))
    .map((c) => ({ c, w: c.closeness * r.range(0.5, 1.5) })).sort((a, b) => b.w - a.w).map((x) => x.c);
  const v = voiceOf(homeCountry(s));
  // whoever was out with you today says so first
  const there = friends.find((c) => c.seen && s.minutes - c.seen < 12 * 60);
  if (there) comments.push({ name: there.name.split(' ')[0], text: r.pick(['That was fun today!', 'Today was a vibe 😂', 'Again soon!', 'Send me the rest of the pics']), t: 0 });
  // family who are close comment; friends comment in order of how close they are (an ex who's sore might too)
  const fam = family.filter((c) => c.closeness > 55 && r.chance(0.3 + c.closeness / 250));
  if (fam.length) comments.push({ name: fam[0].name.split(' ')[0], text: r.pick(v.commentFamily), t: 0 });
  for (const c of friends.filter((c) => c !== there && c.relation !== 'Ex').slice(0, Math.min(3, Math.round(likes / 25)))) if (r.chance(0.35 + c.closeness / 160)) comments.push({ name: c.name.split(' ')[0], text: r.pick(p.quality > 0.4 ? v.commentFriend : v.commentFlat), t: 0 });
  const ex = friends.find((c) => c.relation === 'Ex');
  if (ex && r.chance(0.15)) comments.push({ name: ex.name.split(' ')[0], text: r.pick(['Looking happy 🙂', 'Nice.', 'Wow, okay.']), t: 0 });
  // (strangers: one in forty who like it says something, up to STRANGER_COMMENTS: a huge following isn't a huge save)
  for (let i = 0; i < Math.min(STRANGER_COMMENTS, Math.round(likes / 40)); i++) comments.push({ name: `${r.pick(HANDLES[homeCountry(s)])}${r.pick(['_', '.', ''])}${r.int(1, 999)}`, text: r.pick(voiceOf(homeCountry(s)).commentStranger), t: 0 });
  }
  const waves = 6;
  for (let w = 0; w < waves; w++) {
    const share = w === 0 ? 0.45 : 0.55 / (waves - 1);
    soc.queue.push({ at: s.minutes + Math.round(5 + w * w * 9 + r.int(0, 10)), postId: post.id, likes: Math.round(likes * share), follows: Math.round(follows * share) });
  }
  comments.forEach((c, i) => soc.queue.push({ at: s.minutes + 8 + i * r.int(6, 25), postId: post.id, likes: 0, follows: 0, comment: c }));

  s.needs.fun = Math.min(100, s.needs.fun + 8);
  s.skills.social = Math.min(100, s.skills.social + 0.2 + p.quality * 0.4);
  if (soc.posts.length === 1) s.history.push({ t: s.minutes, text: 'Posted a first photo.' });
  // what you post is in your gallery too
  for (const id of [p.id, ...(p.more ?? [])]) if (!galleryOf(s).some((x) => x.id === id)) galleryOf(s).unshift({ id, t: s.minutes, place: p.place, takenBy: p.takenBy, quality: p.quality, ...(p.reel && id === p.id ? { reel: true } : {}) });
  return [...advance(s, 5), ...progress(s, 'post')];
}

/**
 * The game's people's likes and comments, put away (once, NPC_REACTIONS off): on your posts (kept as npcLikes and
 * npcComments; your counts start again from the players') and on the feed's pages (theirs made-up: just yours shown).
 * The ones still on their way are dropped, follows kept.
 */
export function archiveNpcReactions(s: LifeState) {
  if (NPC_REACTIONS || !s.social || s.social.npcArchived) return;
  const soc = s.social;
  soc.npcArchived = true;
  for (const p of soc.posts) { p.npcLikes = (p.npcLikes ?? 0) + p.likes; p.likes = 0; p.npcComments = [...(p.npcComments ?? []), ...p.comments.filter((c) => !c.mine)]; p.comments = p.comments.filter((c) => c.mine); }
  soc.queue = soc.queue.filter((q) => q.follows > 0).map((q) => ({ at: q.at, postId: q.postId, likes: 0, follows: q.follows }));
  for (const p of (s as LifeState & { feed?: { posts: { likes: number; liked?: boolean; comments: Comment[]; npcLikes?: number; npcComments?: Comment[]; author: { kind: string } }[] } }).feed?.posts ?? []) {
    if (p.author.kind === 'player' || p.author.kind === 'you') continue;
    p.npcLikes = p.likes; p.likes = p.liked ? 1 : 0;
    p.npcComments = p.comments.filter((c) => !c.mine); p.comments = p.comments.filter((c) => c.mine);
  }
}

/** Deliver reactions that are due (called from the life loop). */
export function deliverSocial(s: LifeState, events: LifeEvent[]) {
  const soc = s.social;
  if (!soc || !soc.queue.length) return;
  const due = soc.queue.filter((q) => q.at <= s.minutes);
  if (!due.length) return;
  soc.queue = soc.queue.filter((q) => q.at > s.minutes);
  let likes = 0, follows = 0;
  const said: Comment[] = [];
  for (const q of due) {
    const post = soc.posts.find((p) => p.id === q.postId);
    if (!post) continue;
    post.likes += q.likes; likes += q.likes;
    post.newFollowers += q.follows; follows += q.follows;
    if (q.comment) { post.comments.push({ ...q.comment, t: s.minutes }); said.push(q.comment); }
  }
  for (const p of soc.posts) if (p.comments.length > POST_COMMENTS) p.comments.splice(0, p.comments.length - POST_COMMENTS); // (the newest are what you read)
  soc.followers += follows;
  // being seen feels good
  s.needs.social = Math.min(100, s.needs.social + Math.min(6, likes / 15 + said.length * 2));
  if (likes || follows) events.push({ t: s.minutes, kind: 'social', text: `${likes ? `${likes} ${likes === 1 ? 'like' : 'likes'}` : ''}${likes && follows ? ' · ' : ''}${follows ? `${follows} new ${follows === 1 ? 'follower' : 'followers'}` : ''}` });
  for (const c of said) events.push({ t: s.minutes, kind: 'social', text: `${c.name}: ${c.text}` });
}

// --- your gallery: every picture you kept (posted or not) --------------------------------------------------------

export interface GalleryItem { id: string; t: number; place: string; takenBy: string; quality: number; /** a video (a reel), its first frame the picture */ reel?: boolean; /** a photo of something you did (activityphotos.ts): which */ activity?: string; /** the caption it suggests when you post it */ caption?: string }
export const galleryOf = (s: LifeState): GalleryItem[] => (s.gallery ??= []);
/** Keep a picture without posting it. */
export function saveToGallery(s: LifeState, x: GalleryItem) { if (!galleryOf(s).some((g) => g.id === x.id)) galleryOf(s).unshift(x); }
/** Delete it from the gallery (a post that used it keeps its copy). */
export function removeFromGallery(s: LifeState, id: string) { s.gallery = galleryOf(s).filter((g) => g.id !== id); }
